import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { Draft } from 'immer';
import { useCallback } from 'react';
import { useDependencyGraphProvider } from '../../context/DependencyGraphProvider';
import { useGlobalAppStateStore } from '../../stores/app/GlobalAppStateStore';
import { useGlobalStateStore } from '../../stores/GlobalStateStore';
import { useGetInterfaceStoreState } from '../../stores/InterfaceStore';
import { getOriginalBlockId, isRuntimeBlockId } from '../../utils/runTimeBlocks';
import { ComputeContextKeys } from '../computeContext/const';
import { useGetAndSetRunTimeBlockState } from '../useGetAndSetRunTimeBlockState';
import { useGetBlockStateId } from '../useGetBlockStateId';

function useSetBlockState({
  originalBlockId,
  runtimeBlockKey,
}: {
  originalBlockId: string | undefined;
  runtimeBlockKey?: string;
}) {
  const { blockStateId } = useGetBlockStateId({
    originalBlockId,
    runtimeBlockKey,
  });
  const { getInterfaceStoreState } = useGetInterfaceStoreState();
  const {
    actions: { notifyDependencyChange },
  } = useDependencyGraphProvider();
  const { updateBlockState: updateBlockStateStore } = useGlobalStateStore().hooks.useActions();
  const { setSessionStorage } = useGlobalAppStateStore().hooks.useActions();

  // in the case of !runTimeBlockKey, originalBlockState is the same as runTimeBlockState
  const originalBlockState = useGlobalStateStore().hooks.block.useDetails(originalBlockId);

  const runTimeBlockState = useGetAndSetRunTimeBlockState({
    originalBlockState,
    blockStateId,
    runtimeBlockKey,
  });

  const updateBlockState = useCallback(
    (setter: (blockState: Draft<BlockStateUnionType>) => void) => {
      if (!blockStateId) return;

      updateBlockStateStore(blockStateId, setter);
      notifyDependencyChange({
        id: isRuntimeBlockId(blockStateId) ? getOriginalBlockId(blockStateId) : blockStateId,
      });
    },
    [blockStateId, notifyDependencyChange, updateBlockStateStore],
  );

  const updateBlockSessionState = useCallback(
    (state: unknown) => {
      if (!originalBlockId) return;
      const interfaceId = getInterfaceStoreState().interfaceRecord.id;
      const activeInterfacePageId = getInterfaceStoreState().activeInterfacePageId;
      setSessionStorage({
        operation: 'SET_IN_BLOCK_STATE',
        payload: {
          path: [originalBlockId],
          value: state,
          interfaceId,
          interfacePageId: activeInterfacePageId,
        },
      });

      notifyDependencyChange({
        id: ComputeContextKeys.SESSION_STORAGE,
      });
    },
    [getInterfaceStoreState, notifyDependencyChange, originalBlockId, setSessionStorage],
  );

  return {
    blockStateId,
    blockState: runtimeBlockKey ? runTimeBlockState : originalBlockState,
    updateBlockState,
    updateBlockSessionState,
  };
}

export default useSetBlockState;

export type UseSetBlockStateReturnType = ReturnType<typeof useSetBlockState>;
